import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { DateTime } from 'luxon';

const AlakartOtonomGrid = ({
    weekDates,
    generatedDays,
    userOverrides,
    onSlotClick,
    activeDate,
    isExpired,
    categories,
    onEditDay,
    onApproveDay,
    onRemoveSlot,
    approvedDays,
    lunchPreference
}) => {
    const [editingDays, setEditingDays] = useState({});
    const dayRefs = useRef({});

    const lunchMode = String(lunchPreference?.mode || '').trim();
    const lunchExtraServingText = lunchPreference?.extraServingSize === 'family_size'
        ? 'aile kadar'
        : String(Number(lunchPreference?.extraServingSize || 0) || '').trim();

    const lunchModeTone = {
        extra_dinner_portion: 'border-amber-500/25 bg-amber-500/10 text-amber-100',
        leftover_only: 'border-slate-500/20 bg-slate-500/10 text-slate-100',
        light_practical_lunch: 'border-sky-500/25 bg-sky-500/10 text-sky-100',
        separate_lunch_plan: 'border-rose-500/25 bg-rose-500/10 text-rose-100'
    };

    const lunchModeLabelMap = {
        extra_dinner_portion: {
            eyebrow: 'Öğlen akşamdan',
            title: lunchExtraServingText
                ? `Akşam planı +${lunchExtraServingText} öğlen porsiyonu ile hesaplanır`
                : 'Akşam planı öğlen için fazla porsiyonla hesaplanır',
            text: 'Öğlen için ayrı tarif açılmaz; sistem akşam pişirme miktarını büyütür.',
            icon: 'local_dining'
        },
        leftover_only: {
            eyebrow: 'Öğlen kalanlarla',
            title: 'Ayrı öğlen menüsü açılmaz',
            text: 'Sistem bu gün için kalanları değerlendiren yumuşak hatırlatma davranışını korur.',
            icon: 'schedule'
        },
        light_practical_lunch: {
            eyebrow: 'Hafif / pratik öğlen',
            title: 'Öğlen için düşük eforlu adaylar beklenir',
            text: 'Yan yemek havuzu ve hızlı adaylar öne çıkar; akşam planı kopyalanmaz.',
            icon: 'bolt'
        },
        separate_lunch_plan: {
            eyebrow: 'Bağımsız öğlen',
            title: 'Öğlen akşamdan ayrı üretilir',
            text: 'Öğlen önerisi 7 günlük akşam akışından türetilmez; ayrı bir karar hattı çalışır.',
            icon: 'restaurant'
        }
    };

    useEffect(() => {
        if (activeDate && dayRefs.current[activeDate]) {
            dayRefs.current[activeDate].scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }
    }, [activeDate]);

    const toggleEdit = (dateKey) => {
        setEditingDays((prev) => ({
            ...prev,
            [dateKey]: !prev[dateKey]
        }));
    };

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            {weekDates.map((dayItem, idx) => {
                const dateObj = DateTime.fromISO(dayItem.date);
                const dateKey = dateObj.toISODate();
                const dayPlan = generatedDays?.[dateKey] || {};
                const isToday = dateKey === DateTime.now().toISODate();
                const dayName = dateObj.setLocale('tr').toFormat('cccc').toUpperCase();
                const isEditing = editingDays[dateKey];
                const isApproved = approvedDays?.includes(dateKey);
                const dayOverrides = userOverrides?.[dateKey] || {};

                const mainDish = dayPlan.MAIN || dayPlan.LEGUME || dayPlan.PASTA || dayPlan.CHICKEN || dayPlan.MEAT || dayPlan.SEAFOOD ||
                    Object.values(dayPlan).find((dish) => dish && dish.intelligence);

                const intel = mainDish?.intelligence || { stockRatio: 0, score: 0, label: 'Bekliyor', reasons: [], missingCount: 0 };
                const hasMenu = Object.entries(dayPlan).some(([key, value]) => key !== '_meta' && value && typeof value === 'object');

                // [A.3 UI] Renk artık alışveriş ihtiyacını yansıtır: %100 mutfaktan karşılanıyorsa yeşil,
                // <%100 ise (alışveriş gerekli) amber #F59E0B. Eski eşik (>=80 yeşil) 90'lık "ALIŞVERİŞ GEREKLİ"
                // kartı yanlışlıkla yeşil gösteriyordu.
                const isFullyStocked = hasMenu && intel.stockRatio === 100;
                const colorClass = isApproved ? 'border-green-500/50 bg-green-500/5' :
                    !hasMenu ? 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50' :
                    isFullyStocked ? 'border-green-500/30 bg-green-500/5' :
                    'border-[#F59E0B]/40 bg-[#F59E0B]/5';

                const badgeColor = isApproved ? 'bg-green-600' :
                    !hasMenu ? 'bg-slate-400' :
                    isFullyStocked ? 'bg-green-600/80' :
                    'bg-[#F59E0B]';
                const lunchMeta = lunchModeLabelMap[lunchMode] || null;
                const lunchMetaTone = lunchModeTone[lunchMode] || 'border-slate-500/20 bg-slate-500/10 text-slate-100';

                return (
                    <motion.div
                        key={dateKey}
                        ref={(el) => { dayRefs.current[dateKey] = el; }}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        className={`flex flex-col gap-0 overflow-hidden rounded-[1.5rem] border backdrop-blur-sm transition-all shadow-lg ${colorClass} ${activeDate === dateKey ? 'ring-2 ring-primary/40' : ''}`}
                    >
                        <div className={`flex items-start justify-between p-4 text-white ${badgeColor} transition-colors duration-500`}>
                            <div className="flex flex-col">
                                <span className="text-2xl font-black leading-none">{hasMenu ? `${intel.stockRatio}%` : '--'}</span>
                                <span className="text-[9px] font-black tracking-tight opacity-70">
                                    {dateObj.toFormat('dd.MM')} {dayName}
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-[10px] font-black uppercase tracking-widest">{hasMenu ? intel.label : 'BOŞ GÜN'}</span>
                                {isToday ? <span className="mt-1 rounded-full bg-white/20 px-1.5 py-0.5 text-[8px] font-bold">BUGÜN</span> : null}
                            </div>
                        </div>

                        <div className="border-b border-black/5 bg-black/5 p-3 dark:border-white/5 dark:bg-white/5">
                            <div className="flex flex-col gap-1">
                                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">
                                    {hasMenu ? `%${intel.stockRatio} mutfaktan karşılandı:` : 'Menü eklenmedi'}
                                </span>
                                <div className="flex items-center gap-1">
                                    <span className={`material-symbols-outlined text-xs ${(!hasMenu || intel.stockRatio < 100) ? 'text-amber-500' : 'text-green-500'}`}>
                                        {hasMenu && intel.stockRatio === 100 ? 'verified' : 'shopping_basket'}
                                    </span>
                                    <span className="text-[10px] font-black text-slate-800 dark:text-slate-200">
                                        {!hasMenu ? 'Hazırlık bekleniyor' : (intel.stockRatio === 100 ? 'Mutfak tam uyumlu!' : `${intel.missingCount} ürünle tamamlanır`)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {lunchMeta ? (
                            <div className="border-b border-black/5 px-3 py-2 dark:border-white/5">
                                <div className={`rounded-2xl border px-3 py-2 ${lunchMetaTone}`}>
                                    <div className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-base opacity-90">{lunchMeta.icon}</span>
                                        <div className="flex flex-col">
                                            <span className="text-[9px] font-black uppercase tracking-[0.22em] opacity-70">
                                                {lunchMeta.eyebrow}
                                            </span>
                                            <span className="mt-0.5 text-[11px] font-black leading-4">
                                                {lunchMeta.title}
                                            </span>
                                            <span className="mt-1 text-[10px] font-medium leading-4 opacity-90">
                                                {lunchMeta.text}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div className="min-h-[160px] space-y-1.5 p-2">
                            {categories.filter((cat) => cat.id !== 'ALL').map((cat) => {
                                const data = dayPlan[cat.id];
                                const overrideInfo = dayOverrides?.[cat.id] || null;
                                const isOverridden = Boolean(overrideInfo?.replaced);

                                if (!data) {
                                    return (
                                        <button
                                            key={cat.id}
                                            onClick={() => onSlotClick(dateKey, cat.id, null)}
                                            className="w-full rounded-xl border border-dashed border-slate-300/70 bg-white/20 p-2.5 text-left transition-all hover:border-primary/60 hover:bg-white/40 dark:border-slate-700 dark:bg-slate-800/30 dark:hover:bg-slate-800/60"
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-[9px] font-black uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                                                        {cat.label}
                                                    </span>
                                                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
                                                        Slot boş, tarif seç
                                                    </span>
                                                </div>
                                                <span className="material-symbols-outlined text-lg text-primary">add_circle</span>
                                            </div>
                                        </button>
                                    );
                                }

                                return (
                                    <button
                                        key={cat.id}
                                        onClick={() => onSlotClick(dateKey, cat.id, data)}
                                        className={`group relative w-full rounded-xl border p-2.5 text-left transition-all ${
                                            data.isAllergenSafe === false
                                                ? 'border-red-400/70 bg-red-50/70 hover:bg-red-50 dark:border-red-500/50 dark:bg-red-900/20'
                                                : 'border-white/10 bg-white/40 hover:bg-white/60 dark:bg-slate-800/40 dark:hover:bg-slate-800/60'
                                        }`}
                                    >
                                        <div className="flex flex-col gap-0.5">
                                            {/* [P1] Alerjen uyarısı (evrensel; premium olmayan da görür) */}
                                            {data.isAllergenSafe === false ? (
                                                <div className="mb-1 flex items-center gap-1" title={(data._allergenCollisions || []).join(', ')}>
                                                    <span className="material-symbols-outlined text-[12px] text-red-600 dark:text-red-400">warning</span>
                                                    <span className="text-[9px] font-black uppercase tracking-wider text-red-600 dark:text-red-400">
                                                        Alerjen{(data._allergenCollisions || []).length ? `: ${data._allergenCollisions.join(', ')}` : ' riski'}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {/* [Faz 4.1 "uyar ama göster"] Whitelist güvenli ama içerikte alerjen tespit edildi → uyarı (tarif elenmez) */}
                                            {data.isAllergenSafe !== false && Array.isArray(data._allergenWarnings) && data._allergenWarnings.length > 0 ? (
                                                <div className="mb-1 flex items-center gap-1" title={`İçerikte tespit edildi: ${data._allergenWarnings.join(', ')}`}>
                                                    <span className="material-symbols-outlined text-[12px] text-amber-600 dark:text-amber-400">info</span>
                                                    <span className="text-[9px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-400">
                                                        İçerikte: {data._allergenWarnings.join(', ')}
                                                    </span>
                                                </div>
                                            ) : null}
                                            {isOverridden ? (
                                                <div className="mb-1 flex items-center gap-1">
                                                    <span className="material-symbols-outlined text-[11px] text-primary">edit</span>
                                                    <span className="text-[9px] font-black uppercase tracking-wider text-primary">
                                                        Sen değiştirdin
                                                    </span>
                                                </div>
                                            ) : null}
                                            <span className="line-clamp-1 text-[10px] font-black text-slate-800 dark:text-slate-100">
                                                {data.name}
                                            </span>
                                            {isOverridden && overrideInfo?.previousRecipeName ? (
                                                <span className="line-clamp-1 text-[9px] font-bold text-slate-500 dark:text-slate-400">
                                                    Sistem: {overrideInfo.previousRecipeName}
                                                </span>
                                            ) : null}
                                            <div className="flex items-center gap-1 opacity-50">
                                                <span className="material-symbols-outlined text-[10px]">schedule</span>
                                                <span className="text-[8px] font-black uppercase tracking-tighter">MEDIUM</span>
                                            </div>
                                        </div>
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100">
                                            <span className="material-symbols-outlined text-xs text-primary">chevron_right</span>
                                        </div>
                                    </button>
                                );
                            })}

                            {!hasMenu ? (
                                <div className="flex flex-col items-center justify-center py-6 opacity-20">
                                    <span className="material-symbols-outlined text-3xl">restaurant</span>
                                    <span className="text-[10px] font-bold">Menü Bekleniyor</span>
                                </div>
                            ) : null}
                        </div>

                        <div className="mt-auto flex border-t border-black/5 dark:border-white/5">
                            <button
                                onClick={() => onEditDay(dateKey)}
                                disabled={isApproved}
                                className="flex-1 border-r border-black/5 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-600 disabled:opacity-20 dark:border-white/5 dark:hover:text-slate-200"
                            >
                                DÜZENLE
                            </button>
                            <button
                                onClick={() => !isApproved && onApproveDay && onApproveDay(dateKey)}
                                disabled={isApproved}
                                className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-colors ${isApproved ? 'text-green-500' : 'text-primary hover:bg-primary/5'}`}
                            >
                                {isApproved ? 'ONAYLANDI' : 'ONAYLA'}
                            </button>
                        </div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default AlakartOtonomGrid;
